import React, { useMemo } from "react";
import { faUndo } from "@fortawesome/free-solid-svg-icons/faUndo";
import { isStringTrue } from "@iddataweb/idw-common-ui/utils";
import { BOOLEAN_RADIO_INPUT_OPTIONS } from "@constants/boolean-radio-input-options";
import { resources } from "@constants/hardcoded-strings";
import {
    POST_PII_TO_RP_WEBHOOK_PROPERTY_TYPE_ID,
    RP_WEBHOOK_AUTH_PASS_PROPERTY_TYPE_ID,
    RP_WEBHOOK_AUTH_USER_PROPERTY_TYPE_ID,
    RP_WEBHOOK_CUSTOM_HEADER_PROPERTY_TYPE_ID,
    RP_WEBHOOK_FILE_NAME_PROPERTY_TYPE_ID,
    RP_WEBHOOK_FLAT_ENDPOINT_PROPERTY_TYPE_ID,
    RP_WEBHOOK_TOKEN_OAUTH_ClIENT_ID_PROPERTY_TYPE_ID,
    RP_WEBHOOK_TOKEN_OAUTH_ClIENT_SECRET_PROPERTY_TYPE_ID,
    RP_WEBHOOK_TOKEN_URL_PROPERTY_TYPE_ID,
    RP_WEBHOOK_URL_PROPERTY_TYPE_ID,
    RP_WEBHOOK_WRAPPER_OBJECT_PROPERTY_TYPE_ID
} from "@constants/webhook-constants";
import { LabelWidthTypes } from "@enums/label-width-types";
import { LayoutTypes } from "@enums/layout-types";
import { PageLevelTypes } from "@enums/page-level-types";
import { GeneralAdvancedValidationErrors } from "@idw-types/common/validation/common/general-advanced-validation-errors";
import { FormattedRpServiceConfigurationProperty } from "@idw-types/data/rp-service-configuration";
import { AxnDocumentationLink } from "@idw-types/data/reference-data/axn/axn-documentation-link";
import { FormattedAppliedProfile } from "@idw-types/data/applied-profile/applied-profile";
import { isNullOrUndefined } from "@utils/object-util";
import { getWebhookConfigurationProperties } from "@utils/webhook/webhook-utils";
import { PropertiesSectionDescription } from "../properties-section-header/properties-section-description";
import { ContentPageHeader } from "../../content-page-header/content-page-header";
import { ContentPageBody } from "../../content-page-body/content-page-body";
import { RadioInputFormGroup } from "../../radio-input-form-group/radio-input-form-group";
import { InputFormGroup } from "../../input-form-group/input-form-group";
import { ThemingFormGroupWrapper } from "../../theming-input-form-group/theming-form-group-wrapper";
import styles from "./webhook-configuration-container.less";
import { SecureWebhookTypes } from "@enums/secure-webhook-types";
import { SECURE_WEBHOOK_TYPE_OPTIONS } from "@constants/secure-webhook-type-options";
import { SelectFormGroup } from "@containers/common/select-form-group/select-form-group";
import { SelectOption } from "@idw-types/common";

type WebhookConfigurationContainerProps = {
    isDisabled: boolean;
    validationErrors: GeneralAdvancedValidationErrors;
    appliedProfiles: Array<FormattedAppliedProfile>;
    documentationLinks: Array<AxnDocumentationLink>;
    properties: Array<FormattedRpServiceConfigurationProperty>;
    pageLevel: PageLevelTypes;
    workflowId: string;
    canModifyNonRestrictedProperties: boolean;
    updateProperties: (properties: Array<{ propertyTypeId: string; newValue: string; added: boolean }>) => void;
    onChangeSecurityTypeWebhookEnabled: (newValue: SelectOption) => void;
    onChangeEnableWebhook: (newValue: string) => void;
    onChangeIncludeUserData: (newValue: string) => void;
    onChangeAddWebhookWrapperObject: (newValue: string) => void;
    onChangeSetToFlatEndpoint: (newValue: string) => void;
};

export const WebhookConfigurationContainer = (props: WebhookConfigurationContainerProps) => {
    const webhookConfigurationResources = resources.pages.verificationServiceDetails.tabs.general.subTabs.advanced.sections.webhookConfiguration;

    const {
        validationErrors,
        appliedProfiles,
        properties,
        documentationLinks,
        pageLevel,
        workflowId,
        canModifyNonRestrictedProperties,
        updateProperties,
        onChangeEnableWebhook,
        onChangeSecurityTypeWebhookEnabled,
        onChangeIncludeUserData,
        onChangeAddWebhookWrapperObject,
        onChangeSetToFlatEndpoint,
    } = props;

    const isDisabled = useMemo(() => {
        return (
            props.isDisabled ||
            !canModifyNonRestrictedProperties
        );
    }, [props.isDisabled, canModifyNonRestrictedProperties]);

    const isServiceWithoutWorkflow = useMemo(() => {
        return pageLevel === PageLevelTypes.APIKEY && !workflowId;
    }, [pageLevel, workflowId]);

    const isResetDisabled = useMemo(() => {
        return (
            props.isDisabled ||
            pageLevel === PageLevelTypes.WORKFLOW ||
            isServiceWithoutWorkflow ||
            !canModifyNonRestrictedProperties
        );
    }, [props.isDisabled, pageLevel, isServiceWithoutWorkflow, canModifyNonRestrictedProperties]);

    const webhookConfiguration = useMemo(() => {
        return getWebhookConfigurationProperties(properties, appliedProfiles);
    }, [properties, appliedProfiles]);

    const inheritedWebhookConfiguration = useMemo(() => {
        return getWebhookConfigurationProperties([], appliedProfiles);
    }, [appliedProfiles]);

    const handleResetEnableWebhook = () => {
        onChangeEnableWebhook(`${inheritedIsWebhookEnabled}`);
    };

    const onChangeWebhookTokenUrl = (event: React.ChangeEvent<HTMLInputElement>) => {
        updateProperties([{ propertyTypeId: RP_WEBHOOK_TOKEN_URL_PROPERTY_TYPE_ID, newValue: event.target.value, added: true }]);
    };

    const onChangeWebhookTokenClientID = (event: React.ChangeEvent<HTMLInputElement>) => {
        updateProperties([{ propertyTypeId: RP_WEBHOOK_TOKEN_OAUTH_ClIENT_ID_PROPERTY_TYPE_ID, newValue: event.target.value, added: true }]);
    };

    const onChangeWebhookTokenClientSecret = (event: React.ChangeEvent<HTMLInputElement>) => {
        updateProperties([{ propertyTypeId: RP_WEBHOOK_TOKEN_OAUTH_ClIENT_SECRET_PROPERTY_TYPE_ID, newValue: event.target.value, added: true }]);
    };

    const onChangeRpWebhookAuthUser = (event: React.ChangeEvent<HTMLInputElement>) => {
        updateProperties([{ propertyTypeId: RP_WEBHOOK_AUTH_USER_PROPERTY_TYPE_ID, newValue: event.target.value, added: true }]);
    };

    const onChangeRpWebhookAuthPass = (event: React.ChangeEvent<HTMLInputElement>) => {
        updateProperties([{ propertyTypeId: RP_WEBHOOK_AUTH_PASS_PROPERTY_TYPE_ID, newValue: event.target.value, added: true }]);
    };

    const handleHeaderAuthChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const propertyValue = (webhookConfiguration.rpWebhookCustomHeaderPropertyValue || '').split(':');
        const updatedAuth = event.target.value || '';
        const updatedValue = propertyValue[1] || '';

        let newPropertyValue = '';
        if (updatedAuth && updatedValue) {
            newPropertyValue = `${updatedAuth}:${updatedValue}`;
        } else if (updatedAuth) {
            newPropertyValue = `${updatedAuth}:`;
        } else if (updatedValue) {
            newPropertyValue = `:${updatedValue}`;
        }

        updateProperties([{ propertyTypeId: RP_WEBHOOK_CUSTOM_HEADER_PROPERTY_TYPE_ID, newValue: newPropertyValue, added: true }]);
    };

    const handleValueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const propertyValue = (webhookConfiguration.rpWebhookCustomHeaderPropertyValue || '').split(':');
        const updatedAuth = propertyValue[0] || '';
        const updatedValue = event.target.value || '';

        let newPropertyValue = '';
        if (updatedAuth && updatedValue) {
            newPropertyValue = `${updatedAuth}:${updatedValue}`;
        } else if (updatedAuth) {
            newPropertyValue = `${updatedAuth}:`;
        } else if (updatedValue) {
            newPropertyValue = `:${updatedValue}`;
        }

        updateProperties([{ propertyTypeId: RP_WEBHOOK_CUSTOM_HEADER_PROPERTY_TYPE_ID, newValue: newPropertyValue, added: true }]);
    };

    const onChangeRpWebhookUrl = (event: React.ChangeEvent<HTMLInputElement>) => {
        updateProperties([{ propertyTypeId: RP_WEBHOOK_URL_PROPERTY_TYPE_ID, newValue: event.target.value, added: true }]);
    };
    const handleResetWebhookUrl = () => {
        updateProperties([{ propertyTypeId: RP_WEBHOOK_URL_PROPERTY_TYPE_ID, newValue: null, added: false }]);
    };

    const handleResetIncludeUserDataEnabled = () => {
        updateProperties([{ propertyTypeId: POST_PII_TO_RP_WEBHOOK_PROPERTY_TYPE_ID, newValue: null, added: false }]);
    };

    const handleResetAddWebhookWrapperObject = () => {
        updateProperties([{ propertyTypeId: RP_WEBHOOK_WRAPPER_OBJECT_PROPERTY_TYPE_ID, newValue: null, added: false }]);
    };

    const handleResetSetToFlatEndpoint = () => {
        updateProperties([{ propertyTypeId: RP_WEBHOOK_FLAT_ENDPOINT_PROPERTY_TYPE_ID, newValue: null, added: false }]);
    };

    const onChangeRpWebhookWrapperObjectName = (event: React.ChangeEvent<HTMLInputElement>) => {
        updateProperties([{ propertyTypeId: RP_WEBHOOK_WRAPPER_OBJECT_PROPERTY_TYPE_ID, newValue: event.target.value, added: true }]);
    };

    const handleResetWebhookWrapperObjectName = () => {
        updateProperties([{ propertyTypeId: RP_WEBHOOK_WRAPPER_OBJECT_PROPERTY_TYPE_ID, newValue: null, added: false }]);
    };

    const onChangeRpWebhookFileName = (event: React.ChangeEvent<HTMLInputElement>) => {
        updateProperties([{ propertyTypeId: RP_WEBHOOK_FILE_NAME_PROPERTY_TYPE_ID, newValue: event.target.value, added: true }]);
    };

    const handleResetWebhookFileName = () => {
        updateProperties([{ propertyTypeId: RP_WEBHOOK_FILE_NAME_PROPERTY_TYPE_ID, newValue: null, added: false }]);
    };


    const handleResetWebhookAuthUser = () => {
        updateProperties([{ propertyTypeId: RP_WEBHOOK_AUTH_USER_PROPERTY_TYPE_ID, newValue: null, added: false }]);
    };



    const handleResetWebhookAuthPass = () => {
        updateProperties([{ propertyTypeId: RP_WEBHOOK_AUTH_PASS_PROPERTY_TYPE_ID, newValue: null, added: false }]);
    };

    const handleResetWebhookTokenUrl = () => {
        updateProperties([{ propertyTypeId: RP_WEBHOOK_TOKEN_URL_PROPERTY_TYPE_ID, newValue: null, added: false }]);
    };

    const handleResetWebhookTokenClientID = () => {
        updateProperties([{ propertyTypeId: RP_WEBHOOK_TOKEN_OAUTH_ClIENT_ID_PROPERTY_TYPE_ID, newValue: null, added: false }]);
    };

    const handleResetWebhookTokenClientSecret = () => {
        updateProperties([{ propertyTypeId: RP_WEBHOOK_TOKEN_OAUTH_ClIENT_SECRET_PROPERTY_TYPE_ID, newValue: null, added: false }]);
    };

    const handleResetCustomHeader = () => {
        updateProperties([{ propertyTypeId: RP_WEBHOOK_CUSTOM_HEADER_PROPERTY_TYPE_ID, newValue: null, added: false }]);
    }


    const isWebhookEnabled = useMemo(() => {
        return (
            !isNullOrUndefined(webhookConfiguration.postToRPWebhookPropertyValue) &&
            isStringTrue(webhookConfiguration.postToRPWebhookPropertyValue)
        );
    }, [webhookConfiguration.postToRPWebhookPropertyValue]);

    const inheritedIsWebhookEnabled = useMemo(() => {
        return (
            !isNullOrUndefined(inheritedWebhookConfiguration.postToRPWebhookPropertyValue) &&
            isStringTrue(inheritedWebhookConfiguration.postToRPWebhookPropertyValue)
        );
    }, [inheritedWebhookConfiguration.postToRPWebhookPropertyValue]);

    const isIncludeUserDataEnabled = useMemo(() => {
        return (
            !isNullOrUndefined(webhookConfiguration.postPiiToRPWebhookPropertyValue) &&
            isStringTrue(webhookConfiguration.postPiiToRPWebhookPropertyValue)
        );
    }, [webhookConfiguration.postPiiToRPWebhookPropertyValue]);

    const inheritedIsIncludeUserDataEnabled = useMemo(() => {
        return (
            !isNullOrUndefined(inheritedWebhookConfiguration.postPiiToRPWebhookPropertyValue) &&
            isStringTrue(inheritedWebhookConfiguration.postPiiToRPWebhookPropertyValue)
        );
    }, [inheritedWebhookConfiguration.postPiiToRPWebhookPropertyValue]);

    const isWebhookWrapperObjectEnabled = useMemo(() => {
        return !isNullOrUndefined(webhookConfiguration.rpWebhookWrapperObjectPropertyValue);
    }, [webhookConfiguration.rpWebhookWrapperObjectPropertyValue]);

    const inheritedIsWebhookWrapperObjectEnabled = useMemo(() => {
        return !isNullOrUndefined(inheritedWebhookConfiguration.rpWebhookWrapperObjectPropertyValue);
    }, [inheritedWebhookConfiguration.rpWebhookWrapperObjectPropertyValue]);

    const isSetToFlatEndpointEnabled = useMemo(() => {
        return (
            !isNullOrUndefined(webhookConfiguration.rpWebhookFlatEndpointPropertyValue) &&
            isStringTrue(webhookConfiguration.rpWebhookFlatEndpointPropertyValue)
        );
    }, [webhookConfiguration.rpWebhookFlatEndpointPropertyValue]);

    const inheritedIsSetToFlatEndpointEnabled = useMemo(() => {
        return (
            !isNullOrUndefined(inheritedWebhookConfiguration.rpWebhookFlatEndpointPropertyValue) &&
            isStringTrue(inheritedWebhookConfiguration.rpWebhookFlatEndpointPropertyValue)
        );
    }, [inheritedWebhookConfiguration.rpWebhookFlatEndpointPropertyValue]);
    const documentationLink = useMemo(() => {
        if (documentationLinks.length) {
            const documentationLink = documentationLinks.find(doc => doc.shortName === 'webhook-configuration-widget');
            if (documentationLink) {
                return documentationLink.url;
            }
        }

        return '';
    }, [documentationLinks]);

    const isWebhookEnabledResetDisabled = useMemo(() => {
        return isResetDisabled || (inheritedIsWebhookEnabled === isWebhookEnabled);
    }, [isResetDisabled, inheritedIsWebhookEnabled, isWebhookEnabled]);

    const isWebhookUrlResetDisabled = useMemo(() => {
        return (
            isResetDisabled ||
            !inheritedWebhookConfiguration.rpWebhookUrlPropertyValue ||
            webhookConfiguration.rpWebhookUrlPropertyValue === inheritedWebhookConfiguration.rpWebhookUrlPropertyValue
        );
    }, [
        isResetDisabled,
        webhookConfiguration.rpWebhookUrlPropertyValue,
        inheritedWebhookConfiguration.rpWebhookUrlPropertyValue
    ]);

    const isWebhookAuthUserResetDisabled = useMemo(() => {
        return (
            isResetDisabled ||
            !inheritedWebhookConfiguration.rpWebhookAuthUserPropertyValue ||
            webhookConfiguration.rpWebhookAuthUserPropertyValue === inheritedWebhookConfiguration.rpWebhookAuthUserPropertyValue
        );
    }, [
        isResetDisabled,
        webhookConfiguration.rpWebhookAuthUserPropertyValue,
        inheritedWebhookConfiguration.rpWebhookAuthUserPropertyValue
    ]);

    const isWebhookAuthPassResetDisabled = useMemo(() => {
        return (
            isResetDisabled ||
            !inheritedWebhookConfiguration.rpWebhookAuthPassPropertyValue ||
            webhookConfiguration.rpWebhookAuthPassPropertyValue === inheritedWebhookConfiguration.rpWebhookAuthPassPropertyValue
        );
    }, [
        isResetDisabled,
        webhookConfiguration.rpWebhookAuthPassPropertyValue,
        inheritedWebhookConfiguration.rpWebhookAuthPassPropertyValue
    ]);


    const isWebhookTokenClientIDResetDisabled = useMemo(() => {
        return (
            isResetDisabled ||
            !inheritedWebhookConfiguration.rpWebhookTokenClientIDPropertyValue ||
            webhookConfiguration.rpWebhookTokenClientIDPropertyValue === inheritedWebhookConfiguration.rpWebhookTokenClientIDPropertyValue
        );
    }, [
        isResetDisabled,
        webhookConfiguration.rpWebhookTokenClientIDPropertyValue,
        inheritedWebhookConfiguration.rpWebhookTokenClientIDPropertyValue
    ]);
    const isWebhookTokenClientSecretResetDisabled = useMemo(() => {
        return (
            isResetDisabled ||
            !inheritedWebhookConfiguration.rpWebhookTokenClientSecretPropertyValue ||
            webhookConfiguration.rpWebhookTokenClientSecretPropertyValue === inheritedWebhookConfiguration.rpWebhookTokenClientSecretPropertyValue
        );
    }, [
        isResetDisabled,
        webhookConfiguration.rpWebhookTokenClientSecretPropertyValue,
        inheritedWebhookConfiguration.rpWebhookTokenClientSecretPropertyValue
    ]);

    const isWebhookTokenUrlResetDisabled = useMemo(() => {
        return (
            isResetDisabled ||
            !inheritedWebhookConfiguration.rpWebhookTokenUrlPropertyValue ||
            webhookConfiguration.rpWebhookTokenUrlPropertyValue === inheritedWebhookConfiguration.rpWebhookTokenUrlPropertyValue
        );
    }, [
        isResetDisabled,
        webhookConfiguration.rpWebhookTokenUrlPropertyValue,
        inheritedWebhookConfiguration.rpWebhookTokenUrlPropertyValue
    ]);

    const selectedSecureWebhookTypeOption = useMemo(() => {
        const authTypeValue = webhookConfiguration.rpWebhookAuthTypePropertyValue || SecureWebhookTypes.NONE;
        return SECURE_WEBHOOK_TYPE_OPTIONS.find(option => option.value === authTypeValue);
    }, [SECURE_WEBHOOK_TYPE_OPTIONS, webhookConfiguration.rpWebhookAuthTypePropertyValue]);

    const isIncludeUserDataEnabledResetDisabled = useMemo(() => {
        return isResetDisabled || (isIncludeUserDataEnabled === inheritedIsIncludeUserDataEnabled);
    }, [isResetDisabled, isIncludeUserDataEnabled, inheritedIsIncludeUserDataEnabled]);

    const isWebhookWrapperObjectEnabledResetDisabled = useMemo(() => {
        return isResetDisabled || (isWebhookWrapperObjectEnabled === inheritedIsWebhookWrapperObjectEnabled);
    }, [isResetDisabled, isWebhookWrapperObjectEnabled, inheritedIsWebhookWrapperObjectEnabled]);

    const isSetToFlatEndpointEnabledResetDisabled = useMemo(() => {
        return isResetDisabled || (isSetToFlatEndpointEnabled === inheritedIsSetToFlatEndpointEnabled);
    }, [isResetDisabled, isSetToFlatEndpointEnabled, inheritedIsSetToFlatEndpointEnabled]);

    const isWebhookFileNameResetDisabled = useMemo(() => {
        return (
            isResetDisabled ||
            !inheritedWebhookConfiguration.rpWebhookFileNamePropertyValue ||
            webhookConfiguration.rpWebhookFileNamePropertyValue === inheritedWebhookConfiguration.rpWebhookFileNamePropertyValue
        );
    }, [
        isResetDisabled,
        webhookConfiguration.rpWebhookFileNamePropertyValue,
        inheritedWebhookConfiguration.rpWebhookFileNamePropertyValue
    ]);

    const isWebhookWrapperObjectNameResetDisabled = useMemo(() => {
        return (
            isResetDisabled ||
            !inheritedWebhookConfiguration.rpWebhookWrapperObjectPropertyValue ||
            webhookConfiguration.rpWebhookWrapperObjectPropertyValue === inheritedWebhookConfiguration.rpWebhookWrapperObjectPropertyValue
        );
    }, [
        isResetDisabled,
        webhookConfiguration.rpWebhookWrapperObjectPropertyValue,
        inheritedWebhookConfiguration.rpWebhookWrapperObjectPropertyValue
    ]);

    return (
        <div>
            <ContentPageHeader title={webhookConfigurationResources.title} />
            <ContentPageBody isMaxWidth={true}>
                <PropertiesSectionDescription description={webhookConfigurationResources.description} documentationLink={documentationLink} />
                <div className={styles.webhookConfigurationContainer}>
                    <ThemingFormGroupWrapper
                        currentThemingPropertyValue={isWebhookEnabled}
                        inheritedThemingPropertyValue={inheritedIsWebhookEnabled}
                        pageLevel={pageLevel}
                        hideComparison={isServiceWithoutWorkflow || pageLevel === PageLevelTypes.WORKFLOW}
                    >
                        <RadioInputFormGroup
                            controlId={'enable-webhook-radio-input'}
                            automationId={'enable-webhook-radio-input'}
                            disabled={isDisabled || (!isServiceWithoutWorkflow && pageLevel === PageLevelTypes.APIKEY && inheritedIsWebhookEnabled)}
                            isRequired={false}
                            label={webhookConfigurationResources.inputs.enableWebook.label}
                            layout={LayoutTypes.COLUMN}
                            labelWidth={LabelWidthTypes.LARGE}
                            value={isWebhookEnabled ? BOOLEAN_RADIO_INPUT_OPTIONS[0].value : BOOLEAN_RADIO_INPUT_OPTIONS[1].value}
                            options={BOOLEAN_RADIO_INPUT_OPTIONS}
                            onChange={onChangeEnableWebhook}
                            afterLabelTooltipText={webhookConfigurationResources.inputs.enableWebook.tooltip}
                            afterLabelIcon={isWebhookEnabledResetDisabled ? null : faUndo}
                            afterLabelIconOnClick={handleResetEnableWebhook}
                        />
                    </ThemingFormGroupWrapper>
                    {isWebhookEnabled && (
                        <>
                            <ThemingFormGroupWrapper
                                currentThemingPropertyValue={webhookConfiguration.rpWebhookUrlPropertyValue}
                                inheritedThemingPropertyValue={inheritedWebhookConfiguration.rpWebhookUrlPropertyValue}
                                pageLevel={pageLevel}
                                hideComparison={isServiceWithoutWorkflow || pageLevel === PageLevelTypes.WORKFLOW}
                            >
                                <InputFormGroup
                                    controlId={'webhook-url-input'}
                                    automationId={'webhook-url-input'}
                                    label={webhookConfigurationResources.inputs.webhookUrl.label}
                                    ariaLabel={webhookConfigurationResources.inputs.webhookUrl.label}
                                    onChange={onChangeRpWebhookUrl}
                                    value={webhookConfiguration.rpWebhookUrlPropertyValue || ''}
                                    disabled={isDisabled}
                                    isRequired={true}
                                    layout={LayoutTypes.COLUMN}
                                    labelWidth={LabelWidthTypes.LARGE}
                                    afterLabelTooltipText={webhookConfigurationResources.inputs.webhookUrl.tooltip}
                                    afterLabelIcon={isWebhookUrlResetDisabled ? null : faUndo}
                                    afterLabelIconOnClick={handleResetWebhookUrl}
                                />
                            </ThemingFormGroupWrapper>
                            {validationErrors.rpWebhookUrlError}
                            <SelectFormGroup
                                controlId={`webhook-type-dropdown`}
                                automationId={`webhook-type-dropdown`}
                                className={styles.resultSelect}
                                ariaLabel={"Authorization Type"}
                                label={"Authorization Type"}
                                layout={LayoutTypes.COLUMN}
                                disabled={isDisabled}
                                isClearable={false}
                                isRequired={true}
                                isSearchable={false}
                                labelWidth={LabelWidthTypes.EXTRA_SMALL}
                                onChange={onChangeSecurityTypeWebhookEnabled}
                                options={SECURE_WEBHOOK_TYPE_OPTIONS}
                                afterLabelTooltipText={webhookConfigurationResources.inputs.secureWebhook.tooltip}
                                selectedOption={selectedSecureWebhookTypeOption}
                            />
                            {selectedSecureWebhookTypeOption && selectedSecureWebhookTypeOption.value === SecureWebhookTypes.BASIC && (
                                <>
                                    <ThemingFormGroupWrapper
                                        currentThemingPropertyValue={webhookConfiguration.rpWebhookAuthUserPropertyValue}
                                        inheritedThemingPropertyValue={inheritedWebhookConfiguration.rpWebhookAuthUserPropertyValue}
                                        pageLevel={pageLevel}
                                        hideComparison={isServiceWithoutWorkflow || pageLevel === PageLevelTypes.WORKFLOW}
                                    >

                                        <InputFormGroup
                                            controlId={'webhook-basic-auth-user-input'}
                                            automationId={'webhook-basic-auth-user-input'}
                                            label={webhookConfigurationResources.inputs.webhookAuthUser.label}
                                            ariaLabel={webhookConfigurationResources.inputs.webhookAuthUser.label}
                                            onChange={onChangeRpWebhookAuthUser}
                                            value={webhookConfiguration.rpWebhookAuthUserPropertyValue || ''}
                                            maxLength={30}
                                            disabled={isDisabled}
                                            isRequired={true}
                                            layout={LayoutTypes.COLUMN}
                                            labelWidth={LabelWidthTypes.LARGE}
                                            afterLabelIcon={isWebhookAuthUserResetDisabled ? null : faUndo}
                                            afterLabelIconOnClick={handleResetWebhookAuthUser}
                                        />
                                    </ThemingFormGroupWrapper>
                                    {validationErrors.rpWebhookAuthUserError}

                                    <ThemingFormGroupWrapper
                                        currentThemingPropertyValue={webhookConfiguration.rpWebhookAuthPassPropertyValue}
                                        inheritedThemingPropertyValue={inheritedWebhookConfiguration.rpWebhookAuthPassPropertyValue}
                                        pageLevel={pageLevel}
                                        hideComparison={isServiceWithoutWorkflow || pageLevel === PageLevelTypes.WORKFLOW}
                                    >
                                        <InputFormGroup
                                            controlId={'webhook-basic-auth-pass-input'}
                                            automationId={'webhook-basic-auth-pass-input'}
                                            label={webhookConfigurationResources.inputs.webhookAuthPass.label}
                                            ariaLabel={webhookConfigurationResources.inputs.webhookAuthPass.label}
                                            onChange={onChangeRpWebhookAuthPass}
                                            value={webhookConfiguration.rpWebhookAuthPassPropertyValue || ''}
                                            disabled={isDisabled}
                                            isRequired={true}
                                            layout={LayoutTypes.COLUMN}
                                            labelWidth={LabelWidthTypes.LARGE}
                                            afterLabelIcon={isWebhookAuthPassResetDisabled ? null : faUndo}
                                            afterLabelIconOnClick={handleResetWebhookAuthPass}
                                        />
                                    </ThemingFormGroupWrapper>
                                    {validationErrors.rpWebhookAuthPassError}
                                </>
                            )}

                            {selectedSecureWebhookTypeOption && selectedSecureWebhookTypeOption.value === SecureWebhookTypes.OAUTH && (
                                <>
                                    <ThemingFormGroupWrapper
                                        currentThemingPropertyValue={webhookConfiguration.rpWebhookTokenClientIDPropertyValue}
                                        inheritedThemingPropertyValue={inheritedWebhookConfiguration.rpWebhookTokenClientIDPropertyValue}
                                        pageLevel={pageLevel}
                                        hideComparison={isServiceWithoutWorkflow || pageLevel === PageLevelTypes.WORKFLOW}
                                    >

                                        <InputFormGroup
                                            controlId={'webhook-oauth-client-id-input'}
                                            automationId={'webhook-oauth-client-id-input'}
                                            label={webhookConfigurationResources.inputs.webhookTokenClientID.label}
                                            ariaLabel={webhookConfigurationResources.inputs.webhookTokenClientID.label}
                                            onChange={onChangeWebhookTokenClientID}
                                            value={webhookConfiguration.rpWebhookTokenClientIDPropertyValue || ''}
                                            maxLength={256}
                                            disabled={isDisabled}
                                            isRequired={true}
                                            layout={LayoutTypes.COLUMN}
                                            labelWidth={LabelWidthTypes.LARGE}
                                            afterLabelIcon={isWebhookTokenClientIDResetDisabled ? null : faUndo}
                                            afterLabelIconOnClick={handleResetWebhookTokenClientID}
                                        />
                                    </ThemingFormGroupWrapper>
                                    {validationErrors.rpWebhookClientIDError}


                                    <ThemingFormGroupWrapper
                                        currentThemingPropertyValue={webhookConfiguration.rpWebhookTokenClientSecretPropertyValue}
                                        inheritedThemingPropertyValue={inheritedWebhookConfiguration.rpWebhookTokenClientSecretPropertyValue}
                                        pageLevel={pageLevel}
                                        hideComparison={isServiceWithoutWorkflow || pageLevel === PageLevelTypes.WORKFLOW}
                                    >
                                        <InputFormGroup
                                            controlId={'webhook-oauth-client-secret-input'}
                                            automationId={'webhook-oauth-client-secret-input'}
                                            label={webhookConfigurationResources.inputs.webhookTokenClientSecret.label}
                                            ariaLabel={webhookConfigurationResources.inputs.webhookTokenClientSecret.label}
                                            onChange={onChangeWebhookTokenClientSecret}
                                            value={webhookConfiguration.rpWebhookTokenClientSecretPropertyValue || ''}
                                            maxLength={256}
                                            disabled={isDisabled}
                                            isRequired={true}
                                            layout={LayoutTypes.COLUMN}
                                            labelWidth={LabelWidthTypes.LARGE}
                                            afterLabelIcon={isWebhookTokenClientSecretResetDisabled ? null : faUndo}
                                            afterLabelIconOnClick={handleResetWebhookTokenClientSecret}
                                        />
                                    </ThemingFormGroupWrapper>
                                    {validationErrors.rpWebhookClientSecretError}

                                    <ThemingFormGroupWrapper
                                        currentThemingPropertyValue={webhookConfiguration.rpWebhookTokenUrlPropertyValue}
                                        inheritedThemingPropertyValue={inheritedWebhookConfiguration.rpWebhookTokenUrlPropertyValue}
                                        pageLevel={pageLevel}
                                        hideComparison={isServiceWithoutWorkflow || pageLevel === PageLevelTypes.WORKFLOW}
                                    >
                                        <InputFormGroup
                                            controlId={'webhook-oauth-token-url-input'}
                                            automationId={'webhook-oauth-token-url-input'}
                                            label={webhookConfigurationResources.inputs.webhookTokenUrl.label}
                                            ariaLabel={webhookConfigurationResources.inputs.webhookTokenUrl.label}
                                            onChange={onChangeWebhookTokenUrl}
                                            value={webhookConfiguration.rpWebhookTokenUrlPropertyValue || ''}
                                            disabled={isDisabled}
                                            isRequired={true}
                                            layout={LayoutTypes.COLUMN}
                                            labelWidth={LabelWidthTypes.LARGE}
                                            afterLabelTooltipText={webhookConfigurationResources.inputs.webhookTokenUrl.tooltip}
                                            afterLabelIcon={isWebhookTokenUrlResetDisabled ? null : faUndo}
                                            afterLabelIconOnClick={handleResetWebhookTokenUrl}
                                        />
                                    </ThemingFormGroupWrapper>
                                    {validationErrors.rpWebhookTokenUrlError}
                                </>
                            )}
                            {selectedSecureWebhookTypeOption && selectedSecureWebhookTypeOption.value === SecureWebhookTypes.CUSTOM_HEADER && (
                                <>
                                    <div className={styles.customHeaderContainer}>
                                        <ThemingFormGroupWrapper
                                            currentThemingPropertyValue={webhookConfiguration.rpWebhookCustomHeaderPropertyValue}
                                            inheritedThemingPropertyValue={inheritedWebhookConfiguration.rpWebhookCustomHeaderPropertyValue}
                                            pageLevel={pageLevel}
                                            hideComparison={isServiceWithoutWorkflow || pageLevel === PageLevelTypes.WORKFLOW}
                                        >
                                            <div className={styles.customHeaderInputs}>
                                                <InputFormGroup
                                                    controlId={'webhook-custom-authorization-input'}
                                                    automationId={'webhook-custom-authorization-input'}
                                                    label={webhookConfigurationResources.inputs.webhookCustomHeaderAuth.label}
                                                    ariaLabel={webhookConfigurationResources.inputs.webhookCustomHeaderAuth.label}
                                                    onChange={handleHeaderAuthChange}
                                                    value={(webhookConfiguration.rpWebhookCustomHeaderPropertyValue || '').split(':')[0] || ''}
                                                    disabled={isDisabled}
                                                    isRequired={true}
                                                    layout={LayoutTypes.ROW}
                                                    labelWidth={LabelWidthTypes.SMALL}
                                                    afterLabelIcon={isWebhookEnabledResetDisabled ? null : faUndo}
                                                    afterLabelIconOnClick={handleResetCustomHeader}
                                                />
                                                {validationErrors.rpWebhookCustomHeaderError}
                                                <InputFormGroup
                                                    controlId={'webhook-custom-value-input'}
                                                    automationId={'webhook-custom-value-input'}
                                                    label={webhookConfigurationResources.inputs.webhookCustomValue.label}
                                                    ariaLabel={webhookConfigurationResources.inputs.webhookCustomValue.label}
                                                    onChange={handleValueChange}
                                                    value={(webhookConfiguration.rpWebhookCustomHeaderPropertyValue || '').split(':')[1] || ''}
                                                    disabled={isDisabled}
                                                    isRequired={true}
                                                    layout={LayoutTypes.ROW}
                                                    labelWidth={LabelWidthTypes.SMALL}
                                                    afterLabelIcon={isWebhookEnabledResetDisabled ? null : faUndo}
                                                    afterLabelIconOnClick={handleResetCustomHeader}
                                                />
                                                {validationErrors.rpWebhookCustomValueError}
                                            </div>
                                        </ThemingFormGroupWrapper>
                                    </div>
                                </>
                            )}
                            <div className={styles.advancedConfigurationContainer}>
                                <span>Advanced Configuration</span>
                            </div>
                            <ThemingFormGroupWrapper
                                currentThemingPropertyValue={isIncludeUserDataEnabled}
                                inheritedThemingPropertyValue={inheritedIsIncludeUserDataEnabled}
                                pageLevel={pageLevel}
                                hideComparison={isServiceWithoutWorkflow || pageLevel === PageLevelTypes.WORKFLOW}
                            >
                                <RadioInputFormGroup
                                    controlId={'include-user-data-radio-input'}
                                    automationId={'include-user-data-radio-input'}
                                    disabled={isDisabled}
                                    isRequired={false}
                                    label={webhookConfigurationResources.inputs.includeUserData.label}
                                    labelWidth={LabelWidthTypes.LARGE}
                                    layout={LayoutTypes.COLUMN}
                                    value={isIncludeUserDataEnabled ? BOOLEAN_RADIO_INPUT_OPTIONS[0].value : BOOLEAN_RADIO_INPUT_OPTIONS[1].value}
                                    options={BOOLEAN_RADIO_INPUT_OPTIONS}
                                    onChange={onChangeIncludeUserData}
                                    afterLabelTooltipText={webhookConfigurationResources.inputs.includeUserData.tooltip}
                                    afterLabelIcon={isIncludeUserDataEnabledResetDisabled ? null : faUndo}
                                    afterLabelIconOnClick={handleResetIncludeUserDataEnabled}
                                />
                            </ThemingFormGroupWrapper>
                            <ThemingFormGroupWrapper
                                currentThemingPropertyValue={isSetToFlatEndpointEnabled}
                                inheritedThemingPropertyValue={inheritedIsSetToFlatEndpointEnabled}
                                pageLevel={pageLevel}
                                hideComparison={isServiceWithoutWorkflow || pageLevel === PageLevelTypes.WORKFLOW}
                            >
                                <RadioInputFormGroup
                                    controlId={'set-to-flat-endpoint-radio-input'}
                                    automationId={'set-to-flat-endpoint-radio-input'}
                                    disabled={isDisabled}
                                    isRequired={false}
                                    label={webhookConfigurationResources.inputs.setToFlatEndpoint.label}
                                    labelWidth={LabelWidthTypes.LARGE}
                                    layout={LayoutTypes.COLUMN}
                                    value={isSetToFlatEndpointEnabled ? BOOLEAN_RADIO_INPUT_OPTIONS[0].value : BOOLEAN_RADIO_INPUT_OPTIONS[1].value}
                                    options={BOOLEAN_RADIO_INPUT_OPTIONS}
                                    onChange={onChangeSetToFlatEndpoint}
                                    afterLabelTooltipText={webhookConfigurationResources.inputs.setToFlatEndpoint.tooltip}
                                    afterLabelIcon={isSetToFlatEndpointEnabledResetDisabled ? null : faUndo}
                                    afterLabelIconOnClick={handleResetSetToFlatEndpoint}
                                />
                            </ThemingFormGroupWrapper>
                            <ThemingFormGroupWrapper
                                currentThemingPropertyValue={webhookConfiguration.rpWebhookFileNamePropertyValue}
                                inheritedThemingPropertyValue={inheritedWebhookConfiguration.rpWebhookFileNamePropertyValue}
                                pageLevel={pageLevel}
                                hideComparison={isServiceWithoutWorkflow || pageLevel === PageLevelTypes.WORKFLOW}
                            >
                                <InputFormGroup
                                    controlId={'webhook-file-name-input'}
                                    automationId={'webhook-file-name-input'}
                                    label={webhookConfigurationResources.inputs.webhookFileName.label}
                                    ariaLabel={webhookConfigurationResources.inputs.webhookFileName.label}
                                    onChange={onChangeRpWebhookFileName}
                                    value={webhookConfiguration.rpWebhookFileNamePropertyValue || ''}
                                    maxLength={30}
                                    disabled={isDisabled}
                                    isRequired={false}
                                    layout={LayoutTypes.COLUMN}
                                    labelWidth={LabelWidthTypes.LARGE}
                                    afterLabelTooltipText={webhookConfigurationResources.inputs.webhookFileName.tooltip}
                                    afterLabelIcon={isWebhookFileNameResetDisabled ? null : faUndo}
                                    afterLabelIconOnClick={handleResetWebhookFileName}
                                />
                            </ThemingFormGroupWrapper>
                            {validationErrors.rpWebhookFileNameError}
                            <ThemingFormGroupWrapper
                                currentThemingPropertyValue={isWebhookWrapperObjectEnabled}
                                inheritedThemingPropertyValue={inheritedIsWebhookWrapperObjectEnabled}
                                pageLevel={pageLevel}
                                hideComparison={isServiceWithoutWorkflow || pageLevel === PageLevelTypes.WORKFLOW}
                            >
                                <RadioInputFormGroup
                                    controlId={'add-webhook-wrapper-object-radio-input'}
                                    automationId={'add-webhook-wrapper-object-radio-input'}
                                    disabled={isDisabled}
                                    isRequired={false}
                                    label={webhookConfigurationResources.inputs.addWebhookWrapperObject.label}
                                    labelWidth={LabelWidthTypes.LARGE}
                                    layout={LayoutTypes.COLUMN}
                                    value={isWebhookWrapperObjectEnabled ? BOOLEAN_RADIO_INPUT_OPTIONS[0].value : BOOLEAN_RADIO_INPUT_OPTIONS[1].value}
                                    options={BOOLEAN_RADIO_INPUT_OPTIONS}
                                    onChange={onChangeAddWebhookWrapperObject}
                                    afterLabelTooltipText={webhookConfigurationResources.inputs.addWebhookWrapperObject.tooltip}
                                    afterLabelIcon={isWebhookWrapperObjectEnabledResetDisabled ? null : faUndo}
                                    afterLabelIconOnClick={handleResetAddWebhookWrapperObject}
                                />
                            </ThemingFormGroupWrapper>
                            {isWebhookWrapperObjectEnabled && (
                                <>
                                    <ThemingFormGroupWrapper
                                        currentThemingPropertyValue={webhookConfiguration.rpWebhookWrapperObjectPropertyValue}
                                        inheritedThemingPropertyValue={inheritedWebhookConfiguration.rpWebhookWrapperObjectPropertyValue}
                                        pageLevel={pageLevel}
                                        hideComparison={isServiceWithoutWorkflow || pageLevel === PageLevelTypes.WORKFLOW}
                                    >
                                        <InputFormGroup
                                            controlId={'webhook-wrapper-object-name-input'}
                                            automationId={'webhook-wrapper-object-name-input'}
                                            label={webhookConfigurationResources.inputs.webhookWrapperObjectName.label}
                                            ariaLabel={webhookConfigurationResources.inputs.webhookWrapperObjectName.label}
                                            onChange={onChangeRpWebhookWrapperObjectName}
                                            value={webhookConfiguration.rpWebhookWrapperObjectPropertyValue || ''}
                                            maxLength={30}
                                            disabled={isDisabled}
                                            isRequired={true}
                                            layout={LayoutTypes.COLUMN}
                                            labelWidth={LabelWidthTypes.LARGE}
                                            afterLabelIcon={isWebhookWrapperObjectNameResetDisabled ? null : faUndo}
                                            afterLabelIconOnClick={handleResetWebhookWrapperObjectName}
                                        />
                                    </ThemingFormGroupWrapper>
                                    {validationErrors.rpWebhookWrapperObjectError}
                                </>
                            )}
                        </>
                    )}
                </div>
            </ContentPageBody>
        </div>
    );
};